/**
 * Canonical-origin redirect.
 *
 * The SPA derives its Logto redirect URI from `window.location.origin`, so a
 * deployment reachable under more than one hostname breaks SSO on every
 * hostname that is not registered on the Logto application
 * (`oidc.invalid_redirect_uri`). Staging is such a case: the public name is
 * `staging-ai.sys9.co`, but the old `staging.ai.sys9.co` still resolves on
 * the LAN through the `*.ai.sys9.co` wildcard certificate.
 *
 * When the bundle is built with `VITE_CANONICAL_ORIGIN`, any page load from
 * another origin is bounced to the same path on the canonical one. Local
 * development hosts are exempt so the `ssh -L 4000` QA tunnel and `vite dev`
 * keep working. Unset (prod, CI, mock mode) means no redirect at all.
 */

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

export interface LocationLike {
  origin: string;
  hostname: string;
  pathname: string;
  search: string;
  hash: string;
}

/**
 * Returns the URL to redirect to, or `null` when the current location is
 * already canonical, is a local development host, or no canonical origin is
 * configured. The canonical origin is normalised (trailing slash stripped).
 */
export function resolveCanonicalRedirect(
  current: LocationLike,
  canonicalOrigin: string | undefined
): string | null {
  const canonical = (canonicalOrigin ?? '').trim().replace(/\/+$/, '');
  if (!canonical) return null;
  if (LOCAL_HOSTNAMES.has(current.hostname.toLowerCase())) return null;
  if (current.origin.toLowerCase() === canonical.toLowerCase()) return null;
  return `${canonical}${current.pathname}${current.search}${current.hash}`;
}

/** Side-effecting entry point used by main.tsx; returns true when redirecting. */
export function redirectToCanonicalOrigin(): boolean {
  const target = resolveCanonicalRedirect(
    window.location,
    import.meta.env.VITE_CANONICAL_ORIGIN
  );
  if (!target) return false;
  window.location.replace(target);
  return true;
}
